import { ComponentPropsWithoutRef, forwardRef, MouseEventHandler, ReactNode, Ref } from 'react';
import { FloatingArrow, FloatingFocusManager, FloatingNode, FloatingPortal, useFloating } from '@floating-ui/react';
import { css, cx } from '@linaria/atomic';
import { AnimatePresence, motion, MotionProps } from 'motion/react';
import { theme } from '@frontend/theme';
import { splitStylesProp, type StylesProp } from '../../../helpers/styles-prop';
import type { UsePopoverDialogResponse } from './use-popover-dialog';

type PopoverDialogArrowProps = Omit<ComponentPropsWithoutRef<typeof FloatingArrow>, 'context' | 'ref'>;

type Props = ReturnType<UsePopoverDialogResponse['getDialogProps']> & {
  children: ReactNode;
  className?: string;
  onDoubleClick?: MouseEventHandler<HTMLDialogElement>;
  layoutParent?: boolean | 'position' | 'size';
  css?: StylesProp;
  showArrow?: boolean;
  arrowProps?: PopoverDialogArrowProps;
  returnFocus?: boolean;
} & Pick<MotionProps, 'initial' | 'animate' | 'exit' | 'transition' | 'style'>;

const dialogStyle = css`
  background: white;
  border: none;
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.heavy};
  overflow: auto;
  z-index: ${theme.zIndex.popover};
  &:focus {
    outline: none;
  }
`;

const Dialog = forwardRef(
  (
    {
      isOpen,
      children,
      labelId,
      descriptionId,
      layoutParent,
      className,
      showArrow = false,
      arrowProps,
      ...popoverProps
    }: Omit<Props, 'nodeId' | 'initialFocus'>,
    ref: Ref<HTMLDialogElement>
  ) => {
    const {
      onAnimationStart,
      onDragStart,
      onDragEnd,
      onDrag,
      css: emotionCss,
      style,
      ...motionCompatibleProps
    } = popoverProps;

    const { css: splitCss, className: splitClassName } = splitStylesProp(emotionCss);

    return (
      <motion.dialog
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        ref={ref}
        layout={layoutParent}
        open={isOpen}
        aria-describedby={descriptionId}
        aria-labelledby={labelId}
        className={cx(dialogStyle, className, splitClassName)}
        css={splitCss}
        style={style}
        {...motionCompatibleProps}
      >
        {showArrow && <FloatingArrow ref={popoverProps.arrowRef} context={popoverProps.context} {...arrowProps} />}
        {children}
      </motion.dialog>
    );
  }
);

Dialog.displayName = 'PopoverDialogContent';

export const PopoverDialog = forwardRef(
  (
    { isOpen, nodeId, layoutParent, returnFocus = true, initialFocus, ...props }: Props,
    ref: Ref<HTMLDialogElement> | undefined
  ) => {
    useFloating({ nodeId: nodeId });

    return (
      <FloatingNode id={nodeId}>
        <FloatingPortal>
          <AnimatePresence>
            {isOpen && (
              <FloatingFocusManager context={props.context} initialFocus={initialFocus} returnFocus={returnFocus}>
                <Dialog {...props} isOpen={isOpen} ref={ref} />
              </FloatingFocusManager>
            )}
          </AnimatePresence>
        </FloatingPortal>
      </FloatingNode>
    );
  }
);

PopoverDialog.displayName = 'PopoverDialog';
